<template>
    <Button
        id="settings-menu"
        type="button"
        icon="pi pi-bars"
        severity="secondary"
        @click="toggle"
        aria-haspopup="true"
        aria-controls="overlay_menu"
    />
    <Menu ref="settingsMenu" :model="menuItems" :popup="true">
        <template #item="{ item, props }">
            <a v-ripple class="flex items-center" v-bind="props.action">
                <span v-if="item.icon" :class="item.icon" />
                <img v-if="item.img" :src="item.img" class="profile-img" />
                <span>{{ item.label }}</span>
                <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
                <span
                    v-if="item.shortcut"
                    class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1"
                >
                    {{ item.shortcut }}
                </span>
            </a>
        </template>
    </Menu>
</template>

<script setup lang="ts">
import { useKeybindingsExplanation } from "@/composables/keybindings-explanation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useSteksManager } from "@/stores/steks-store";
import { useUIStore } from "@/stores/ui-store";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useUserManager } from "@/stores/user-store";

const ui = useUIStore();
const keybindings = useKeybindingsExplanation();
const { photoUrl } = useUserManager();

const settingsMenu = ref();
const toggle = (event: Event) => {
    settingsMenu.value.toggle(event);
};
const router = useRouter();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { organizations } = useOrganizationsManager();
const { steks } = useSteksManager();

const menuItems = computed(() => [
    {
        label: "Profile",
        icon: photoUrl.value ? "" : "pi pi-user",
        img: photoUrl.value,
        class: "setting profile",
        command: () => router.push({ name: "profile" }),
    },
    {
        label: ui.darkMode ? "Light mode" : "Dark Mode",
        icon: ui.darkMode ? "pi pi-sun" : "pi pi-moon",
        badge: ui.darkMode ? "" : "BETA",
        command: () => ui.toggleDarkMode(),
    },
    {
        separator: true,
        visible: () =>
            Boolean(organization.value?.name || organizations.value?.length),
    },
    {
        label: "Organization Users",
        icon: "pi pi-users",
        command: () =>
            router.push({
                name: "userManagement",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: () => Boolean(organization.value?.name),
    },
    {
        label: organization.value
            ? "Switch Organization"
            : "Select Organization",
        icon: "pi pi-sync",
        command: () => router.push({ name: "organizationPage" }),
        visible: () =>
            Boolean(organizations.value && organizations.value.length > 1),
    },
    {
        separator: true,
        visible: () =>
            Boolean(organization.value?.name || organizations.value?.length),
    },
    {
        label: "Create new stek",
        icon: "pi pi-plus-circle",
        command: () =>
            router.push({
                name: "createStek",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: () => Boolean(organization.value?.name),
    },
    {
        label: stek.value ? "Switch Stek" : "Select Stek",
        icon: "pi pi-sync",
        command: () =>
            router.push({
                name: "organizationPage",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: () =>
            Boolean(
                organization.value?.name &&
                    steks.value &&
                    steks.value.length > 1
            ),
    },
    { separator: true },
    {
        label: "Help",
        icon: "pi pi-info-circle",
        command: () => router.push({ name: "help" }),
        class: "setting help",
    },
    {
        label: "Keybindings",
        icon: "pi pi-question-circle",
        command: () => keybindings.open(),
    },
]);
</script>

<style lang="scss">
.profile-img {
    width: var(--p-icon-size);
    height: var(--p-icon-size);
    border-radius: 50%;
    object-fit: cover;
}
</style>
